/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React, { FC, useEffect, useState } from "react";
import { useFormik } from "formik";

import clsx from "clsx";
import "../../../assets/styles/custom-style.css";
import { useAuth } from "../../../auth";
import { useNavigate, useParams } from "react-router-dom";
import { UUID } from "crypto";
import { Addons } from "../core/_models";
import { checkDuplicateAddon, getType, updateAddon } from "../core/_requests";
import ListAddons from "./ListAddon";

import { addonsSchema } from "../core/Schema";

interface Type {
  uuid: UUID;
  name: string;
}
type Props = {
  initialValues: Addons;
};

const EditAddons: FC<Props> = ({ initialValues }) => {
        const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [data, setData] = useState<Addons>(initialValues);
  const [loading, setLoading] = useState(false);
  const [Duplicate, setDuplicate] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [activeType, setActiveType] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [type, setType] = useState<Type[]>([]);
  const { id } = useParams();
  const chefId = currentUser?.uuid;
  useEffect(() => {
    getType()
      .then((res) => {
        setType(res?.data?.data);
        // Set the initial value for the Type dropdown
        const initialType = res?.data?.data.find(
          (type: any) => type.uuid === initialValues.typeId
        );
        if (initialType) {
          setActiveType(initialType.name);
          setSelectedType(initialType.name);
        }
      })
      .catch((error) => {
        console.error("Error fetching types:", error);
      });
  }, []);

  const clickCancel = () => {
    navigate("/addons");
  };



  const formik = useFormik<Addons>({
    initialValues,
    validationSchema: addonsSchema,
    onSubmit: async (values, actions) => {
      values.chefId = currentUser?.uuid;
      values.createdBy = currentUser?.chefId;
      values.updatedBy = currentUser?.chefId;
      const updatedData = Object.assign(data, values);
      setLoading(true);
      updateAddon(id, updatedData).then((res) => {
        if (res?.status === true) {
          navigate("/addons/editsuccess", { replace: true });
          setLoading(false);
        } else {
          setLoading(false);
          setErrorMessage(res?.message);
        }
      });
    },
  });

  const handleNameChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    formik.setFieldValue("name", value);
    formik.validateField("name");
    if (value) {
      const response = await checkDuplicateAddon(value, chefId);
      if (response?.isDuplicate) {
        setDuplicate("Addons name already exist");
      } else {
        setDuplicate("");
      }
    } else {
      setDuplicate("");
    }
  };
  return (
    <>
      <section className="container-fluid addContainerFluid">
        <ListAddons />
        <div className="overlay"></div>
        <div className="drawer small">
          <div className="title row">
            <h3 className="col-10 mb-0">Edit Addons</h3>
            <div className="col-2 text-end mb-2">
              <button
                className="btn p-0 close d-inline-flex"
                onClick={clickCancel}
              >
                <i className="material-symbols-outlined">close</i>
              </button>
            </div>
          </div>
          <form
            onSubmit={formik.handleSubmit}
            noValidate
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
          >
            <div className="col-12 mb-3 mt-3">
              <label htmlFor="" className="form-label">
                Addons Name
              </label>
              <input
                type="text"
                className={clsx(
                  "form-control",
                  {
                    "is-invalid": formik.touched.name && formik.errors.name,
                  },
                  {
                    "is-valid": formik.touched.name && !formik.errors.name,
                  }
                )}
                placeholder="Give a addon name"
                {...formik.getFieldProps(`name`)}
              />
              {formik.touched.name && formik.errors.name && (
                <div className="fv-help-block text-danger">
                  {formik.errors.name}
                </div>
              )}
              <span style={{ color: "red" }}>{Duplicate ? Duplicate : ""}</span>
            </div>
            <div className="col-12 mb-3">
              <label htmlFor="" className="form-label">
                Type
              </label>
              <div className="row type-snippet">
                {type.map((item: any, i) => {
                  const isActive = activeType === item?.name;
                  return (
                    <div
                      className={`mt-1 snippet ${isActive ? "active" : ""}`}
                      key={i}
                      onClick={() => {
                        formik.setFieldValue("typeId", item?.uuid);
                        setActiveType(item?.name);
                        setSelectedType(isActive ? "" : item?.name);
                      }}
                    >
                      <span>
                        <img src={item?.icon} alt={item?.name} /> {item?.name}
                      </span>

                      <div className="ico">
                        <img
                          className={isActive ? "tick" : "cross"}
                          src={`/assets/images/${isActive ? "tick" : "cross"
                            }-circle-ico.svg`}
                          alt="ico"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {formik.touched.typeId && formik.errors.typeId && (
                <div className="fv-plugins-message-container">
                  <span role="alert">{formik.errors.typeId}</span>
                </div>
              )}
            </div>
            <div className="col-12 mb-3 mt-3">
              <label htmlFor="" className="form-label">
                Amount ($)
              </label>
              <input
                type="text"
                className={clsx(
                  "form-control",
                  {
                    "is-invalid": formik.touched.amount && formik.errors.amount,
                  },
                  {
                    "is-valid": formik.touched.amount && !formik.errors.amount,
                  }
                )}
                placeholder="Give a addon amount"
                {...formik.getFieldProps(`amount`)}
              />
              {formik.touched.amount && formik.errors.amount && (
                <div className="fv-help-block text-danger">
                  {formik.errors.amount}
                </div>
              )}
            </div>
            <div className="col-12 mt-3">
              <div className="row">
                <div className="col-4"></div>
                <div className="col-8 text-end">
                  <button
                    className="btn btn-cancel rounded-5"
                    onClick={clickCancel}
                  >
                    Cancel
                  </button>
                  <button
                    disabled={formik.isSubmitting || !formik.isValid}
                    className="btn btn-outline-primary rounded-5 me-2"
                  >
                    {!loading && (
                      <span className="indicator-label">Update</span>
                    )}
                    {loading && (
                      <span
                        className="indicator-progress"
                        style={{ display: "block" }}
                      >
                        Please wait...
                        <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>
    </>
  );
};

export default EditAddons;
